import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getActiveClientPolicySnapshot,
  getClientPolicies,
  getClientPolicyRules,
  getEffectiveThresholdsSnapshot,
  saveClientPolicyOverrides,
  savePolicyThresholdValues,
} from '../../services/complianceRules';
import { getRulesEngineUiMessage } from '../../services/rulesEngineErrors';
import { getCoafGroupLabel, getThresholdKeyLabel } from '../../services/complianceTranslations';
import { routePaths } from '../../config/navigation';
import RulesPlanGrid from '../../components/RulesPlan/RulesPlanGrid';

const cloneRules = (rules) => rules.map((rule) => ({ ...rule }));

const getRuleBusinessKey = (rule) => rule?.code || rule?.id;

const getChangeBusinessKey = (change) => change?.code || change?.id;

const collectEditableThresholdKeys = (rules) =>
  Array.from(new Set(
    (rules || [])
      .filter((rule) => rule?.canEditThresholds)
      .flatMap((rule) => rule?.editableThresholdKeys || []),
  ));

const buildPendingChanges = (originalRules, draftRules) => {
  const originalByKey = new Map(
    originalRules.map((rule) => [getRuleBusinessKey(rule), rule]).filter(([k]) => Boolean(k)),
  );

  return draftRules.reduce((changes, draftRule) => {
    const businessKey = getRuleBusinessKey(draftRule);
    const originalRule = originalByKey.get(businessKey);

    if (!originalRule || !originalRule.changeable || originalRule.active === draftRule.active) {
      return changes;
    }

    changes.push({
      id: draftRule.id,
      code: draftRule.code,
      changeable: draftRule.changeable,
      nextActive: draftRule.active,
      rule: originalRule,
    });

    return changes;
  }, []);
};

const applySuccessfulChanges = (rules, successfulChanges) =>
  rules.map((rule) => {
    const change = successfulChanges.find(
      (item) => getChangeBusinessKey(item.change) === getRuleBusinessKey(rule),
    );
    return change ? { ...rule, active: change.change.nextActive } : rule;
  });

const formatFailedRuleCodes = (failedChanges) =>
  failedChanges.map((item) => item?.change?.code || item?.change?.id).filter(Boolean).join(', ');

const formatFailedSaveStatuses = (failedChanges) => {
  const messages = new Map();
  failedChanges.forEach((item) => {
    const status = item?.error?.status || item?.error?.response?.status;
    const message = getRulesEngineUiMessage(item?.error, '');
    if (status && message && !messages.has(status)) messages.set(status, message);
  });
  return Array.from(messages.values()).join(' ');
};

const buildFailedSaveItems = (failedChanges) =>
  failedChanges.map((item) => ({
    code: item?.change?.code || item?.change?.id || 'N/A',
    status: item?.error?.status || item?.error?.response?.status || null,
    message: getRulesEngineUiMessage(item?.error, 'Falha ao salvar override.'),
  }));

const formatFailedThresholdKeys = (failedChanges) => {
  const keys = failedChanges.map((item) => item?.change?.key).filter(Boolean);
  return keys.map((key) => getThresholdKeyLabel(key)).join(', ');
};

const isClientPolicyNotFoundError = (error) => {
  const status = error?.response?.status || error?.status;
  const rawMessage = String(
    error?.response?.data?.error
      || error?.response?.data?.message
      || error?.message
      || ''
  ).toLowerCase();

  return status === 404 && rawMessage.includes('client policy not found');
};

const parseNumericValue = (value, valueType) => {
  if (valueType === 'integer') {
    const v = Number.parseInt(String(value), 10);
    return Number.isNaN(v) ? null : v;
  }
  if (valueType === 'float') {
    const v = Number.parseFloat(String(value));
    return Number.isNaN(v) ? null : v;
  }
  return null;
};

const buildThresholdLimitError = (key, limits, draftValue) => {
  if (!limits || typeof limits !== 'object') return '';
  const { valueType } = limits;
  if (valueType !== 'integer' && valueType !== 'float') return '';
  const num = parseNumericValue(draftValue, valueType);
  if (num === null) return `${getThresholdKeyLabel(key)}: valor inválido para tipo ${valueType}.`;
  if (limits.min !== null && limits.min !== undefined && num < Number(limits.min))
    return `${getThresholdKeyLabel(key)}: valor mínimo é ${limits.min}.`;
  if (limits.max !== null && limits.max !== undefined && num > Number(limits.max))
    return `${getThresholdKeyLabel(key)}: valor máximo é ${limits.max}.`;
  return '';
};

const extractSuccessfulThresholdIds = (successfulChanges) =>
  successfulChanges.reduce((acc, item) => {
    if (item?.change?.key && item?.thresholdValueId) acc[item.change.key] = item.thresholdValueId;
    return acc;
  }, {});

const filterByGroup = (rules, group) =>
  rules.filter((r) => r.coafGroup === group || (!r.coafGroup && group === 'Outros'));

export default function RulesPlanGroupEdit() {
  const { group } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPolicyId = React.useMemo(() => new URLSearchParams(location.search).get('policyId') || '', [location.search]);
  const selectedOrganizationId = React.useMemo(() => new URLSearchParams(location.search).get('organizationId') || '', [location.search]);

  const [loading, setLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [saveMessage, setSaveMessage] = React.useState('');
  const [saveErrorMessage, setSaveErrorMessage] = React.useState('');
  const [failedSaveItems, setFailedSaveItems] = React.useState([]);
  const [savingRuleChanges, setSavingRuleChanges] = React.useState(false);
  const [savingThresholdChanges, setSavingThresholdChanges] = React.useState(false);
  const [policy, setPolicy] = React.useState(null);
  const [originalRules, setOriginalRules] = React.useState([]);
  const [draftRules, setDraftRules] = React.useState([]);
  const [thresholdValuesBaseline, setThresholdValuesBaseline] = React.useState({});
  const [thresholdValuesDraft, setThresholdValuesDraft] = React.useState({});
  const [, setThresholdValueIdsByKey] = React.useState({});
  const [thresholdLimitsByKey, setThresholdLimitsByKey] = React.useState({});
  const [deactivationReasonsByRuleKey, setDeactivationReasonsByRuleKey] = React.useState({});
  const [groupChangeReason, setGroupChangeReason] = React.useState('');

  const effectiveOrganizationId = React.useMemo(
    () => selectedOrganizationId || policy?.organizationId || policy?.raw?.organization_id || '',
    [policy, selectedOrganizationId],
  );

  React.useEffect(() => {
    if (!group) return;

    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setErrorMessage('');

        let effectivePolicy = null;
        let effectiveRules = [];

        if (selectedPolicyId) {
          const policies = await getClientPolicies({ scopeType: 'organization', organizationId: selectedOrganizationId });
          if (!isMounted) return;

          const selectedPolicy = policies.find((item) => item.id === selectedPolicyId) || null;

          if (!selectedPolicy) {
            setPolicy(null);
            setOriginalRules([]);
            setDraftRules([]);
            setThresholdValuesBaseline({});
            setThresholdValuesDraft({});
            setThresholdValueIdsByKey({});
            setThresholdLimitsByKey({});
            setErrorMessage('A política selecionada não pertence à organização escolhida. Volte e selecione uma política válida.');
            return;
          }

          try {
            effectiveRules = await getClientPolicyRules(selectedPolicyId, { organizationId: selectedOrganizationId });
            if (!isMounted) return;

            effectivePolicy = selectedPolicy;
          } catch (error) {
            if (!isClientPolicyNotFoundError(error)) throw error;

            const snapshot = await getActiveClientPolicySnapshot({ scopeType: 'organization', organizationId: selectedOrganizationId });
            if (!isMounted) return;
            effectivePolicy = snapshot.policy;
            effectiveRules = snapshot.rules;
          }
        } else {
          const snapshot = await getActiveClientPolicySnapshot({ scopeType: 'organization', organizationId: selectedOrganizationId });
          if (!isMounted) return;
          effectivePolicy = snapshot.policy;
          effectiveRules = snapshot.rules;
        }

        const groupRules = filterByGroup(effectiveRules, group);

        setPolicy(effectivePolicy);
        setOriginalRules(cloneRules(groupRules));
        setDraftRules(cloneRules(groupRules));

        const thresholdKeys = collectEditableThresholdKeys(groupRules);

        if (!thresholdKeys.length) {
          setThresholdValuesBaseline({});
          setThresholdValuesDraft({});
          setThresholdValueIdsByKey({});
          setThresholdLimitsByKey({});
          return;
        }

        const snap = await getEffectiveThresholdsSnapshot(thresholdKeys, '', effectivePolicy?.id || '', '');
        if (!isMounted) return;

        setThresholdValuesBaseline({ ...snap.valuesByKey });
        setThresholdValuesDraft({ ...snap.valuesByKey });
        setThresholdValueIdsByKey({ ...snap.idsByKey });
        setThresholdLimitsByKey({ ...(snap.thresholdLimitsByKey || {}) });
      } catch (error) {
        if (isMounted) {
          setPolicy(null);
          setOriginalRules([]);
          setDraftRules([]);
          setThresholdValuesBaseline({});
          setThresholdValuesDraft({});
          setThresholdValueIdsByKey({});
          setThresholdLimitsByKey({});
          setGroupChangeReason('');
          setErrorMessage(getRulesEngineUiMessage(error));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => { isMounted = false; };
  }, [group, selectedOrganizationId, selectedPolicyId]);

  const pendingChanges = React.useMemo(
    () =>
      buildPendingChanges(originalRules, draftRules).map((change) =>
        change.nextActive
          ? change
          : { ...change, reason: deactivationReasonsByRuleKey[getChangeBusinessKey(change)] || '' },
      ),
    [deactivationReasonsByRuleKey, draftRules, originalRules],
  );

  const pendingThresholdChanges = React.useMemo(() => {
    const allKeys = Array.from(new Set([
      ...Object.keys(thresholdValuesBaseline),
      ...Object.keys(thresholdValuesDraft),
    ]));
    return allKeys.reduce((changes, key) => {
      const base = thresholdValuesBaseline[key] ?? '';
      const draft = thresholdValuesDraft[key] ?? '';
      if (String(base) !== String(draft)) changes.push({ key, baselineValue: base, draftValue: draft });
      return changes;
    }, []);
  }, [thresholdValuesBaseline, thresholdValuesDraft]);

  const hasPendingRuleChanges = pendingChanges.length > 0;
  const hasPendingThresholdChanges = pendingThresholdChanges.length > 0;
  const hasPendingChanges = hasPendingRuleChanges || hasPendingThresholdChanges;
  const canManageGroupState = React.useMemo(
    () => draftRules.some((rule) => rule.changeable),
    [draftRules],
  );

  const handleToggleRule = React.useCallback((ruleId) => {
    setDraftRules((current) =>
      current.map((rule) =>
        rule.id === ruleId && rule.changeable ? { ...rule, active: !rule.active } : rule,
      ),
    );
    setSaveMessage('');
    setSaveErrorMessage('');
    setFailedSaveItems([]);
  }, []);

  const handleThresholdValueChange = React.useCallback((key, value) => {
    setThresholdValuesDraft((current) => ({ ...current, [key]: value }));
  }, []);

  const handleDiscardChanges = React.useCallback(() => {
    setDraftRules(cloneRules(originalRules));
    setThresholdValuesDraft({ ...thresholdValuesBaseline });
    setDeactivationReasonsByRuleKey({});
    setGroupChangeReason('');
    setSaveMessage('');
    setSaveErrorMessage('');
    setFailedSaveItems([]);
  }, [originalRules, thresholdValuesBaseline]);

  const handleSaveRuleChanges = React.useCallback(async () => {
    if (!policy || !hasPendingRuleChanges) return;

    setSavingRuleChanges(true);
    setSaveMessage('');
    setSaveErrorMessage('');
    setFailedSaveItems([]);

    try {
      const invalidDeactivation = pendingChanges.find(
        (change) => !change.nextActive && !String(change.reason || '').trim(),
      );

      if (invalidDeactivation) {
        setSaveErrorMessage(`Informe a justificativa para desativação da regra ${invalidDeactivation.code || invalidDeactivation.id} antes de salvar.`);
        return;
      }

      const summary = await saveClientPolicyOverrides(policy.id, pendingChanges, { organizationId: effectiveOrganizationId });

      if (summary.failed.length) {
        const successfulRules = applySuccessfulChanges(draftRules, summary.successful);
        const failedRuleKeys = new Set(summary.failed.map((item) => getChangeBusinessKey(item.change)));
        const reconciledDraft = successfulRules.map((rule) => {
          if (failedRuleKeys.has(getRuleBusinessKey(rule))) {
            const orig = originalRules.find((r) => getRuleBusinessKey(r) === getRuleBusinessKey(rule));
            return orig ? { ...orig } : rule;
          }
          return rule;
        });

        setOriginalRules((current) => applySuccessfulChanges(current, summary.successful));
        setDraftRules(reconciledDraft);
        setFailedSaveItems(buildFailedSaveItems(summary.failed));
        setSaveErrorMessage(
          `Nem todas as alterações de regras foram salvas. Sucesso: ${summary.successful.length}. Falhas: ${summary.failed.length}. ${formatFailedRuleCodes(summary.failed) ? `Regras com falha: ${formatFailedRuleCodes(summary.failed)}.` : ''} ${formatFailedSaveStatuses(summary.failed) ? `Detalhes: ${formatFailedSaveStatuses(summary.failed)}` : ''}`.trim(),
        );
        return;
      }

      setOriginalRules(cloneRules(draftRules));
      setSaveMessage(`Alterações de regras salvas com sucesso (${summary.successful.length}).`);
    } catch (error) {
      setFailedSaveItems([]);
      setSaveErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSavingRuleChanges(false);
    }
  }, [draftRules, hasPendingRuleChanges, pendingChanges, policy, originalRules, effectiveOrganizationId]);

  const handleGroupStateChange = React.useCallback(async (nextActive) => {
    if (!policy || !group || !canManageGroupState) return;

    const reason = String(groupChangeReason || '').trim();
    if (!reason) {
      setSaveErrorMessage(`Informe a justificativa para ${nextActive ? 'ativar' : 'desativar'} o grupo ${group}.`);
      return;
    }

    setSavingRuleChanges(true);
    setSaveMessage('');
    setSaveErrorMessage('');
    setFailedSaveItems([]);

    try {
      const eligibleRules = draftRules.filter((rule) => rule.changeable);
      if (!eligibleRules.length) {
        setSaveErrorMessage('Nenhuma regra elegível para alteração no grupo.');
        return;
      }

      const groupChanges = draftRules
        .filter((rule) => rule.changeable && rule.active !== nextActive)
        .map((rule) => ({
          id: rule.id,
          code: rule.code,
          changeable: rule.changeable,
          nextActive,
          reason,
          rule: originalRules.find((current) => getRuleBusinessKey(current) === getRuleBusinessKey(rule)) || rule,
        }));

      if (!groupChanges.length) {
        setSaveMessage(`Grupo ${group} já está ${nextActive ? 'ativado' : 'desativado'}.`);
        return;
      }

      const summary = await saveClientPolicyOverrides(policy.id, groupChanges, { organizationId: effectiveOrganizationId });
      if (summary.failed.length) {
        setFailedSaveItems(buildFailedSaveItems(summary.failed));
        setSaveErrorMessage(
          `Nem todas as alterações do grupo foram salvas. Sucesso: ${summary.successful.length}. Falhas: ${summary.failed.length}. ${formatFailedRuleCodes(summary.failed) ? `Regras com falha: ${formatFailedRuleCodes(summary.failed)}.` : ''}`.trim(),
        );
        return;
      }

      const changedRuleKeys = new Set(groupChanges.map((change) => getChangeBusinessKey(change)));
      const nextRules = draftRules.map((rule) => (
        changedRuleKeys.has(getRuleBusinessKey(rule)) ? { ...rule, active: nextActive } : rule
      ));
      setDraftRules(cloneRules(nextRules));
      setOriginalRules(cloneRules(nextRules));
      setDeactivationReasonsByRuleKey({});
      setGroupChangeReason('');
      setSaveMessage(`Grupo ${group} ${nextActive ? 'ativado' : 'desativado'} com sucesso.`);
    } catch (error) {
      setSaveErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSavingRuleChanges(false);
    }
  }, [canManageGroupState, draftRules, group, groupChangeReason, originalRules, policy, effectiveOrganizationId]);

  const handleDeactivationReasonChange = React.useCallback((ruleKey, value) => {
    if (!ruleKey) return;
    setDeactivationReasonsByRuleKey((current) => ({ ...current, [ruleKey]: value }));
    setSaveMessage('');
    setSaveErrorMessage('');
    setFailedSaveItems([]);
  }, []);

  const handleSaveThresholdChanges = React.useCallback(async () => {
    if (!policy || !hasPendingThresholdChanges) return;

    setSavingThresholdChanges(true);
    setSaveMessage('');
    setSaveErrorMessage('');
    setFailedSaveItems([]);

    try {
      const invalidChange = pendingThresholdChanges.find((change) => {
        const limits = thresholdLimitsByKey[change.key];
        return Boolean(buildThresholdLimitError(change.key, limits, change.draftValue));
      });

      if (invalidChange) {
        const limits = thresholdLimitsByKey[invalidChange.key];
        setSaveErrorMessage(buildThresholdLimitError(invalidChange.key, limits, invalidChange.draftValue));
        return;
      }

      const thresholdSummary = await savePolicyThresholdValues(policy.id, pendingThresholdChanges, 'Ajuste de limiar via tela de regras', { organizationId: effectiveOrganizationId });

      const applySuccessfulThresholds = (successfulChanges) => {
        const successfulKeys = new Set(successfulChanges.map((item) => item?.change?.key).filter(Boolean));
        const successfulIds = extractSuccessfulThresholdIds(successfulChanges);

        setThresholdValuesBaseline((current) => {
          const next = { ...current };
          successfulKeys.forEach((key) => { next[key] = thresholdValuesDraft[key]; });
          return next;
        });

        if (Object.keys(successfulIds).length) {
          setThresholdValueIdsByKey((current) => ({ ...current, ...successfulIds }));
        }
      };

      if (thresholdSummary.failed.length) {
        if (thresholdSummary.successful.length) applySuccessfulThresholds(thresholdSummary.successful);
        const failedKeys = formatFailedThresholdKeys(thresholdSummary.failed);
        setSaveErrorMessage(
          `Nem todas as alterações de limiares foram salvas. Sucesso: ${thresholdSummary.successful.length}. Falhas: ${thresholdSummary.failed.length}. ${failedKeys ? `Chaves com falha: ${failedKeys}.` : ''}`.trim(),
        );
        return;
      }

      if (thresholdSummary.successful.length) {
        applySuccessfulThresholds(thresholdSummary.successful);
        setSaveMessage(`Alterações de limiares salvas com sucesso (${thresholdSummary.successful.length}).`);
      }
    } catch (error) {
      setSaveErrorMessage(getRulesEngineUiMessage(error));
    } finally {
      setSavingThresholdChanges(false);
    }
  }, [
    hasPendingThresholdChanges,
    pendingThresholdChanges,
    policy,
    thresholdLimitsByKey,
    thresholdValuesDraft,
    effectiveOrganizationId,
  ]);

  const backToOverviewPath = React.useMemo(() => {
    const query = new URLSearchParams();
    if (effectiveOrganizationId) query.set('organizationId', effectiveOrganizationId);
    const queryString = query.toString();
    return queryString ? `${routePaths.rulesPlan}?${queryString}` : routePaths.rulesPlan;
  }, [effectiveOrganizationId]);

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Stack spacing={2}>
        {!loading && !errorMessage && !policy ? (
          <Alert severity="info">Esta organização não possui policy ativa para edição.</Alert>
        ) : null}

        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            variant="outlined"
            size="small"
            disableRipple
            onClick={() => navigate(backToOverviewPath)}
          >
            ← Regras da Policy
          </Button>
          <Typography variant="h6">
            {getCoafGroupLabel(group)}
          </Typography>
        </Stack>

        <RulesPlanGrid
          loading={loading}
          errorMessage={errorMessage}
          saveMessage={saveMessage}
          saveErrorMessage={saveErrorMessage}
          failedSaveItems={failedSaveItems}
          savingRuleChanges={savingRuleChanges}
          savingThresholdChanges={savingThresholdChanges}
          editable={Boolean(policy)}
          hasPendingChanges={hasPendingChanges}
          pendingChanges={pendingChanges}
          pendingThresholdChanges={pendingThresholdChanges}
          policy={policy}
          rules={draftRules}
          deactivationReasonsByRuleKey={deactivationReasonsByRuleKey}
          thresholdValuesDraft={thresholdValuesDraft}
          thresholdLimitsByKey={thresholdLimitsByKey}
          riskThresholdKeys={[]}
          onToggleRule={handleToggleRule}
          onDeactivationReasonChange={handleDeactivationReasonChange}
          groupChangeReason={groupChangeReason}
          onGroupChangeReasonChange={setGroupChangeReason}
          onEnableGroup={() => handleGroupStateChange(true)}
          onDisableGroup={() => handleGroupStateChange(false)}
          canManageGroupState={canManageGroupState}
          onThresholdValueChange={handleThresholdValueChange}
          onDiscardChanges={handleDiscardChanges}
          onSaveRuleChanges={handleSaveRuleChanges}
          onSaveThresholdChanges={handleSaveThresholdChanges}
        />
      </Stack>
    </Box>
  );
}
